import _ from 'lodash';
import axios from 'axios';
import { SDLUtil, StringUtil } from '@/utils';
import { sysUrlList, accessType, initSysUrlList } from '@/constants/SysLinkConstants';

/**
 * param: url (String)
 * return: whether the parameter is correct URL (Boolean)
 * exception: nothing
 * description: 파라미터가 올바른 URL인지 체크 (check whether the parameter is correct URL)
 * since: 2023.11.13
 */
export const isCorrectUrl = (param) => {
  if (_.isEmpty(sysUrlList)) initSysUrlList(); // URL init

  let result = _.some(_.map(sysUrlList, 'url'), (item) => _.isEqual(param, item));

  if (result === false) {
    document.location = `${SDLUtil.WEB_CONTEXT_PATH}/warning?code=400`;
    return;
  }

  return result;
}

/**
 * param: url (Object)
 * return: {url, systemName} || Boolean (Object || Boolean)
 * exception: nothing
 * description: 파라미터 init (init parameter)
 * since: 2023.12.04
 */
const initParam = (param) => {
  const initParamSysName = (obj) => { // 파라미터의 url에 해당하는 systemName 세팅
    const paramUrl = obj.url;
    const sys = _.find(sysUrlList, (item) => paramUrl.includes(item.url));
    if (sys) {
      return Object.assign(obj,
        {
          systemName: sys.systemName,
          systemLogName: sys.systemLogName,
          isUseScopToken: sys.isUseScopToken,
          isConnectionLog: sys.isConnectionLog,
          accessType: sys.isUseAccessType ? obj.accessType : '',
        });
    }
    return false;
  };

  let defaultParam = {
    url: '',
    isNewTab: true,
    accessType: '',
  };
  if (typeof param === 'string' && param.indexOf('http') !== -1) defaultParam = Object.assign(defaultParam, { url: param }); // param이 String일 경우
  else defaultParam = Object.assign(defaultParam, param); // param이 Object일 경우

  return initParamSysName(defaultParam);
};

/**
 * param: {systemName} (Object)
 * return: token (String)
 * exception: nothing
 * description: 시스템 인터페이스 토큰 조회 (get system interface token)
 * since: 2023.10.26
 */
const generateToken = async (param) => {
  let token;
  await axios
    .get(`${SDLUtil.API_URL}/syslink/sys-link-token?${StringUtil.queryStringfy(param)}`)
    .then(response => {
      ({ token } = response.data);
    })
    .catch(error => {
      console.log(error);
    });
  return token;
};

/**
 * param: param(Object), token(String)
 * return: nothing
 * exception: nothing
 * description: 타 시스템 endpoint로 scop토큰 submit (submit scop token to other system's endpoint)
 * since: 2023.12.20
 */
const submitTokenToEndpoint = (param, token) => {
  const formName = 'sysLinkSubmitForm';

  // 삭제되지 않은 form 존재시 해당 form 삭제
  const previousForm = document.body.querySelector(`form[name="${formName}"]`);
  if (previousForm) document.body.removeChild(previousForm);

  // form 세팅
  const form = document.createElement('form');
  form.setAttribute('name', `${formName}`);
  form.setAttribute('method', 'POST');
  form.setAttribute('action', param.url);
  if (param.isNewTab) form.setAttribute('target', '_blank');

  // form에 토큰 세팅
  const hiddenField = document.createElement('input');
  hiddenField.setAttribute('type', 'hidden');
  hiddenField.setAttribute('name', 'scop-token');
  hiddenField.value = token;

  form.appendChild(hiddenField);
  document.body.appendChild(form);

  form.submit();
  document.body.removeChild(form);
};

/**
 * param: url (String)
 * return: nothing
 * exception: nothing
 * description: 타 시스템으로 이동 (move to other system)
 * since: 2023.10.26
 */
export const moveSysLink = async (p) => {
  // URL init
  if (_.isEmpty(sysUrlList)) initSysUrlList();

  // 토큰 생성
  const token = await generateToken(p);
  if (_.isEmpty(token)) {
    console.log('Empty Token');
    window.location.href = `${SDLUtil.WEB_CONTEXT_PATH}/`;
    return;
  }

  submitTokenToEndpoint(p, token);
};

/**
 * param: url (String)
 * return: nothing
 * exception: nothing
 * description: [SCOP토큰 연계하는 시스템 / 단순 이동하는 시스템] 오픈 ([SCOP token linkage system / simple moving system] Open)
 * since: 2024.05.16
 */
export const openSysLink = async (url) => {
  if (_.isEmpty(sysUrlList)) initSysUrlList(); // URL init

  const system = initParam({ url: url, accessType: accessType.ACTION });
  if (system && system.isConnectionLog) await insertConnectionLog(system); // 접속 로그 생성

  if (system && system.isUseScopToken) await moveSysLink(system); // SCOP토큰으로 연계하는 시스템
  else window.open(url.startsWith('http') ? url : `https://${url}`, '_blank'); // 단순 이동하는 시스템
}

/**
 * param: system (Object)
 * return: nothing
 * exception: nothing
 * description: 접속 로그 생성 (insert connection log)
 * since: 2024.05.16
 */
const insertConnectionLog = async (system) => {
  await axios
    .post(`${SDLUtil.API_URL}/syslink/log-connection`, system)
    .then(response => {
      // console.log(response);
    })
    .catch(error => {
      console.log(error);
    });
}

/**
 * param: param (Object)
 * return: nothing
 * exception: nothing
 * description: SCOP 리다이렉션 로그인 (login with SCOP redirection)
 * since: 2024.05.16
 */
export const redirectLogin = async (param) => {
  if (_.isEmpty(sysUrlList)) initSysUrlList(); // URL init

  const defaultParam = {
    accessType: accessType.REDIRECTION,
    isNewTab: false,
  };

  param = { ...defaultParam, ...param };

  const system = initParam(param);
  if (!system) {
    window.location.href = `${SDLUtil.WEB_CONTEXT_PATH}/`;
    return;
  }

  if (system && system.isConnectionLog) await insertConnectionLog(system); // 접속 로그 생성

  if (system && system.isUseScopToken) await moveSysLink(system); // SCOP토큰으로 연계하는 시스템
}

export default {
  moveSysLink,
  isCorrectUrl,
  openSysLink,
  redirectLogin,
};



// WEBPACK FOOTER //
// ./src/service/syslinkService.js